import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  X,
  Sun,
  Moon,
  Search,
  ChevronDown,
  LogOut,
  User,
  Wallet,
  CalendarCheck,
  MessageSquare,
  ShieldCheck,
  Sparkle,
  Info,
  Store,
  Heart,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useUnifiedAuth } from '../../hooks/useUnifiedAuth';
import { useTheme } from '../../contexts/ThemeContext';
import LanguageSwitcher from '../LanguageSwitcher';
import NotificationBell from '../NotificationBell';
import AuthModal from '../AuthModal';
import { discoveryCategories } from './navConfig';
import { useScrolled } from './motion';

const SiteHeader = () => {
  const { user: legacyUser, logout: legacyLogout } = useAuth();
  const { user: unifiedUser, logout: unifiedLogout, userRole: unifiedUserRole, loading: unifiedLoading } = useUnifiedAuth();
  const { t } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const scrolled = useScrolled(10);

  const user = unifiedUser || legacyUser;
  const logout = unifiedUser ? unifiedLogout : legacyLogout;
  const role = (unifiedUserRole || 'patient').toLowerCase();
  // A stored token means a session is being restored — don't flash "Sign In"
  const authResolving = !user && (unifiedLoading || !!localStorage.getItem('sessionToken'));

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [isInfluencer, setIsInfluencer] = useState(false);
  const accountRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setShowAccount(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('touchstart', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('touchstart', onClick);
    };
  }, []);

  useEffect(() => {
    setShowDrawer(false);
    setShowAccount(false);
  }, [location.pathname, location.search]);

  // iOS needs a position-fixed lock; `overflow: hidden` alone lets the page
  // scroll behind the drawer and restores at the wrong offset on close.
  useEffect(() => {
    if (!showDrawer) return undefined;
    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [showDrawer]);

  useEffect(() => {
    const checkInfluencer = async () => {
      const token = localStorage.getItem('sessionToken');
      if (!token || !user) {
        setIsInfluencer(false);
        return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/influencer/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setIsInfluencer(Boolean(data.is_influencer));
        }
      } catch (e) {
        setIsInfluencer(false);
      }
    };
    checkInfluencer();
  }, [user]);

  const go = (path) => {
    setShowDrawer(false);
    setShowAccount(false);
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    setIsInfluencer(false);
    setShowDrawer(false);
    setShowAccount(false);
    navigate('/');
    setTimeout(() => window.location.reload(), 100);
  };

  const isActive = (to) => `${location.pathname}${location.search}` === to;

  const accountLinks = [
    { id: 'messages', label: t('nav.messages', 'Messages'), icon: MessageSquare, to: '/messages' },
    {
      id: 'appointments',
      label: role === 'provider' ? t('siteNav.manager', 'Manager') : t('nav.myAppointments', 'My Appointments'),
      icon: CalendarCheck,
      to: role === 'provider' ? '/provider-dashboard' : '/appointments',
    },
    { id: 'profile', label: t('nav.myProfile', 'My Profile'), icon: User, to: '/profile' },
    { id: 'wallet', label: t('nav.myWallet', 'My Wallet'), icon: Wallet, to: '/wallet' },
  ];

  if (isInfluencer) {
    accountLinks.push({
      id: 'influencer',
      label: t('siteNav.influencer', 'Influencer Dashboard'),
      icon: Sparkle,
      to: '/influencer',
    });
  }
  if (role === 'admin') {
    accountLinks.push({
      id: 'admin',
      label: t('siteNav.adminPanel', 'Admin Panel'),
      icon: ShieldCheck,
      to: '/admin',
    });
  }

  return (
    <>
      <header
        data-testid="navbar-container"
        className={`sticky-surface sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'border-b border-gray-200/70 dark:border-white/10 shadow-[0_10px_40px_-24px_rgba(15,23,42,0.35)]'
            : 'border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between gap-3 transition-all duration-500 ${scrolled ? 'h-16' : 'h-[4.75rem]'}`}>
            {/* Brand */}
            <button
              onClick={() => go('/')}
              data-testid="navbar-logo-link"
              className="group flex items-center gap-3 shrink-0"
              style={{ direction: 'ltr' }}
            >
              <span className="relative grid place-items-center w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-emerald-500 shadow-lg shadow-blue-600/25 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:rotate-3">
                <Heart className="w-5 h-5 text-white" strokeWidth={2.4} />
                <span className="absolute inset-0 rounded-2xl bg-white/25 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </span>
              <span className="text-start">
                <span className="block font-display text-base md:text-lg font-extrabold tracking-tight text-gray-900 dark:text-white leading-none">
                  {t('header.title', 'EnayaHub')}
                </span>
                <span className="hidden sm:block text-[11px] font-medium tracking-wide text-gray-500 dark:text-gray-400 mt-1">
                  {t('siteNav.tagline', 'Health · Beauty · Wellness')}
                </span>
              </span>
            </button>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-0.5 lg:gap-1">
              {discoveryCategories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => go(cat.to)}
                    data-testid={`nav-link-${cat.id}`}
                    data-active={isActive(cat.to)}
                    className={`nav-underline relative flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-medium transition-colors duration-300 hover:text-gray-900 dark:hover:text-white ${
                      cat.priority === 2 ? 'hidden xl:flex' : ''
                    } ${isActive(cat.to) ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}
                  >
                    <Icon className={`w-4 h-4 ${cat.accent}`} strokeWidth={2} />
                    {t(cat.shortKey || cat.labelKey, cat.shortFallback || cat.fallback)}
                  </button>
                );
              })}
              <button
                onClick={() => go('/about')}
                data-testid="nav-link-about"
                className="nav-underline relative hidden lg:flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-300"
              >
                <Info className="w-4 h-4 text-gray-400" strokeWidth={2} />
                {t('nav.aboutUs', 'About Us')}
              </button>
            </nav>

            {/* Right cluster */}
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <button
                onClick={() => go('/search')}
                data-testid="navbar-search-button"
                aria-label={t('common.search', 'Search')}
                className="hidden sm:grid place-items-center w-10 h-10 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors duration-300"
              >
                <Search className="w-[18px] h-[18px]" />
              </button>

              <button
                onClick={toggleTheme}
                data-testid="navbar-theme-toggle"
                aria-label={t('header.theme.toggle', 'Toggle Theme')}
                className="grid place-items-center w-10 h-10 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors duration-300"
              >
                {isDark ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
              </button>

              <div className="hidden md:block" data-testid="navbar-language-switcher">
                <LanguageSwitcher />
              </div>

              {user && <NotificationBell />}

              {user ? (
                <div className="relative" ref={accountRef}>
                  <button
                    onClick={() => setShowAccount((v) => !v)}
                    data-testid="navbar-user-menu"
                    className="flex items-center gap-2 rounded-full border border-gray-200 dark:border-white/10 bg-white/70 dark:bg-white/5 ps-1.5 pe-2.5 py-1.5 hover:border-blue-300 dark:hover:border-blue-500/40 transition-colors duration-300"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <span className="grid place-items-center w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 text-white text-xs font-bold">
                        {(user.name || 'U')[0]}
                      </span>
                    )}
                    <span className="hidden lg:block max-w-[110px] truncate text-[13px] font-medium text-gray-800 dark:text-gray-100">
                      {user.name}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showAccount ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {showAccount && (
                    <div
                      data-testid="navbar-user-menu-dropdown"
                      className="absolute end-0 mt-3 w-60 rounded-2xl glass-panel p-2 origin-top"
                    >
                      <div className="px-3 py-2 mb-1 border-b border-gray-200/70 dark:border-white/10">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{role}</p>
                      </div>
                      {accountLinks.map((link) => {
                        const Icon = link.icon;
                        return (
                          <button
                            key={link.id}
                            onClick={() => go(link.to)}
                            data-testid={`navbar-account-${link.id}`}
                            className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-white/10 transition-colors duration-200"
                          >
                            <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            {link.label}
                          </button>
                        );
                      })}
                      <button
                        onClick={handleLogout}
                        data-testid="navbar-logout-button"
                        className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 mt-1 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors duration-200 border-t border-gray-200/70 dark:border-white/10"
                      >
                        <LogOut className="w-4 h-4" />
                        {t('nav.logout', 'Logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : authResolving ? (
                <div
                  data-testid="navbar-auth-loading"
                  className="h-10 w-24 animate-pulse rounded-full bg-gray-200/80 dark:bg-white/10"
                />
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  data-testid="navbar-signin-button"
                  className="btn-shine relative rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 px-4 md:px-5 py-2.5 text-[13px] font-semibold text-white shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-emerald-500/25 transition-shadow duration-300"
                >
                  {t('signIn', 'Sign In')}
                </button>
              )}

              <button
                onClick={() => setShowDrawer(true)}
                data-testid="navbar-mobile-toggle"
                aria-label={t('nav.menu', 'Menu')}
                className="md:hidden grid place-items-center w-11 h-11 rounded-full text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {showDrawer && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-gray-950/50 backdrop-blur-sm"
            onClick={() => setShowDrawer(false)}
            data-testid="mobile-drawer-backdrop"
          />
          <div
            data-testid="mobile-drawer"
            className="absolute inset-y-0 end-0 w-[86%] max-w-sm bg-white dark:bg-gray-950 border-s border-gray-200 dark:border-white/10 shadow-2xl overflow-y-auto pretty-scroll"
          >
            <div className="flex items-center justify-between px-5 h-[4.75rem] border-b border-gray-100 dark:border-white/10">
              <span className="font-display text-lg font-extrabold text-gray-900 dark:text-white">
                {t('header.title', 'EnayaHub')}
              </span>
              <button
                onClick={() => setShowDrawer(false)}
                data-testid="mobile-drawer-close"
                aria-label={t('common.close', 'Close')}
                className="grid place-items-center w-10 h-10 rounded-full text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              <div className="space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">
                  {t('siteNav.discover', 'Discover')}
                </p>
                {discoveryCategories.map((cat, i) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => go(cat.to)}
                      data-testid={`mobile-nav-link-${cat.id}`}
                      className="drawer-item w-full flex items-center gap-3 rounded-2xl px-3 py-3.5 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                      style={{ animationDelay: `${i * 45}ms` }}
                    >
                      <span className={`grid place-items-center w-9 h-9 rounded-xl ${cat.chip}`}>
                        <Icon className={`w-4 h-4 ${cat.accent}`} />
                      </span>
                      {t(cat.labelKey, cat.fallback)}
                    </button>
                  );
                })}
              </div>

              {user && (
                <div className="space-y-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">
                    {t('siteNav.account', 'Account')}
                  </p>
                  {accountLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <button
                        key={link.id}
                        onClick={() => go(link.to)}
                        data-testid={`mobile-account-${link.id}`}
                        className="w-full flex items-center gap-3 rounded-2xl px-3 py-3.5 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                      >
                        <span className="grid place-items-center w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-500/10">
                          <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </span>
                        {link.label}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="space-y-1.5 pt-1 border-t border-gray-100 dark:border-white/10">
                <button
                  onClick={() => go('/about')}
                  data-testid="mobile-nav-link-about"
                  className="w-full flex items-center gap-3 rounded-2xl px-3 py-3.5 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <span className="grid place-items-center w-9 h-9 rounded-xl bg-gray-100 dark:bg-white/5">
                    <Info className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                  </span>
                  {t('nav.aboutUs', 'About Us')}
                </button>
                <button
                  onClick={() => go('/pricing')}
                  data-testid="mobile-nav-link-providers"
                  className="w-full flex items-center gap-3 rounded-2xl px-3 py-3.5 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <span className="grid place-items-center w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-500/10">
                    <Store className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </span>
                  {t('siteNav.forProviders', 'For Providers')}
                </button>
              </div>

              <div className="pt-1">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">
                  {t('header.language.switch', 'Switch Language')}
                </p>
                <LanguageSwitcher />
              </div>

              {user ? (
                <button
                  onClick={handleLogout}
                  data-testid="mobile-logout-button"
                  className="w-full flex items-center justify-center gap-2 rounded-2xl bg-rose-50 dark:bg-rose-500/10 px-4 py-3.5 text-sm font-semibold text-rose-600 dark:text-rose-400"
                >
                  <LogOut className="w-4 h-4" />
                  {t('nav.logout', 'Logout')}
                </button>
              ) : authResolving ? (
                <div
                  data-testid="mobile-auth-loading"
                  className="h-12 w-full animate-pulse rounded-2xl bg-gray-200/80 dark:bg-white/10"
                />
              ) : (
                <button
                  onClick={() => {
                    setShowDrawer(false);
                    setShowAuthModal(true);
                  }}
                  data-testid="mobile-signin-button"
                  className="w-full rounded-2xl bg-gradient-to-r from-blue-600 to-emerald-500 px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25"
                >
                  {t('signIn', 'Sign In')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
};

export default SiteHeader;
